import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DataService } from './data.service';
import { EncounterClaimStatusSyncService } from './encounter-claim-status-sync.service';
import { resolveEncounterScheduleStatus } from '../helpers/encounter-schedule-status.util';
import type { EngageReferral, EngageReferralLastActivity } from './engage-activity.service';
import {
  appendPatientDirectoryFiltersToUrl,
  parsePatientDirectoryOrLegacyReferralsPayload,
  phiPatientDirectoryListGetUrl,
  type PatientDirectoryFilterValues,
} from '../utils/phi-patient-directory-list.util';
import type { ReferralAttachOrOpenPreviewResult } from '../models/referral-attach-or-open-preview.model';
/** Typeahead row shape (mapped from PHI patient directory list rows). */
export interface ReferralSearchResult {
  id: string;
  /** Clinic PHI patient id when present on the directory row (distinct from referral {@link id}). */
  patientId?: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  createdAt: string;
}

/** US phone → 10 digits for Engage referral create/update/search. */
export function normalizeEngageReferralPhoneDigits(
  raw: string | null | undefined
): string {
  const digits = String(raw ?? "").replace(/\D/g, "");
  const ten =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return ten.length === 10 ? ten : "";
}

/** Engage GET /api/v1/tags/search */
export interface EngageTagSearchResult {
  id: string;
  lead_status: string[];
  tag_name: string;
  tag_slug: string;
  tag_event: boolean;
  eventTypeId: string;
  createdAt: string;
}

/**
 * Clinic-v3 Engage feature needs a small subset of Engage-v2 APIs.
 * This service mirrors the method surface used by the cloned Engage components.
 *
 * NOTE: In clinic-v3, `environment.apiBaseUrl` points to PHI APIs and
 * `environment.engageApiBaseUrl` points to Engage APIs (referrals/activity/events).
 */
@Injectable({
  providedIn: 'root',
})
export class EngagePatientService {
  /** Last successful GET `/referrals/{id}/details` per id (session memory — optimistic UI + dedupe). */
  private readonly referralDetailsResponseCache = new Map<string, unknown>();
  /** Coalesce concurrent identical referral-details GETs. */
  private readonly referralDetailsInflight = new Map<string, Observable<unknown>>();

  constructor(
    private readonly _dataService: DataService,
    private readonly _encounterClaimStatusSyncService: EncounterClaimStatusSyncService
  ) {}

  /**
   * Most recent successful referral-details payload for the lookup id (if any).
   * Used to paint Engage headers immediately while a fresh request runs.
   */
  peekCachedReferralDetails(
    patientId?: string | null,
    referralId?: string | null
  ): unknown | undefined {
    const id = this.referralDetailsLookupId(patientId, referralId);
    if (!id) {
      return undefined;
    }
    return this.referralDetailsResponseCache.get(id);
  }

  /** GET `/referrals/{id}/details` path id — patient id when present, else referral id. */
  referralDetailsLookupId(
    patientId?: string | null,
    referralId?: string | null
  ): string {
    return String(patientId ?? "").trim() || String(referralId ?? "").trim();
  }

  /** Drop cached GET details (e.g. after PATCH updates). */
  invalidateCachedReferralDetails(
    patientId?: string | null,
    referralId?: string | null
  ): void {
    const keys = new Set(
      [
        this.referralDetailsLookupId(patientId, referralId),
        String(patientId ?? "").trim(),
        String(referralId ?? "").trim(),
      ].filter(Boolean)
    );
    for (const id of keys) {
      this.referralDetailsResponseCache.delete(id);
      this.referralDetailsInflight.delete(id);
    }
  }

  // ----------------------------
  // Engage referral APIs
  // ----------------------------

  /**
   * GET `api/v1/referrals/{id}/details` — pass clinic patient id when available (not referral id).
   */
  getReferralDetailsById(
    patientId?: string | null,
    referralId?: string | null
  ): Observable<any> {
    const id = this.referralDetailsLookupId(patientId, referralId);
    if (!id) {
      return of(null);
    }
    const existing = this.referralDetailsInflight.get(id);
    if (existing) {
      return existing;
    }
    const url = `${environment.engageApiBaseUrl}api/v1/referrals/${encodeURIComponent(id)}/details`;
    const req$ = this._dataService.get(url).pipe(
      tap((res) => {
        this.referralDetailsResponseCache.set(id, res);
      }),
      finalize(() => {
        this.referralDetailsInflight.delete(id);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.referralDetailsInflight.set(id, req$);
    return req$;
  }

  /**
   * PATCH `api/v1/referrals/{id}` — pass clinic patient id when available (not referral id).
   */
  updateReferral(
    referralId: string,
    payload: any,
    patientId?: string | null
  ): Observable<any> {
    const id = this.referralDetailsLookupId(patientId, referralId);
    if (!id) {
      return of(null);
    }
    const url = `${environment.engageApiBaseUrl}api/v1/referrals/${encodeURIComponent(id)}`;
    return this._dataService.patch(url, payload).pipe(
      tap(() => {
        this.invalidateCachedReferralDetails(patientId, referralId);
      })
    );
  }

  /**
   * Resolve an existing Engage referral id by phone when POST /referrals/clinic returns 409.
   * Uses legacy `GET …/referrals/search?q=` (digits-only).
   */
  findEngageReferralIdByPhone(phoneDigits: string): Observable<string | null> {
    const target = normalizeEngageReferralPhoneDigits(phoneDigits);
    if (!target) {
      return of(null);
    }
    const url = `${environment.engageApiBaseUrl}api/v1/referrals/search?q=${encodeURIComponent(
      target
    )}`;
    return this._dataService.get(url).pipe(
      map((res: unknown) => {
        const list = this.parseEngageReferralSearchRows(res);
        for (const row of list) {
          const id = this.extractEngageReferralSearchRowId(row);
          if (!id) {
            continue;
          }
          if (this.engageReferralSearchRowPhoneDigits(row) === target) {
            return id;
          }
        }
        const first = list[0];
        return first ? this.extractEngageReferralSearchRowId(first) : null;
      }),
      catchError(() => of(null))
    );
  }

  private parseEngageReferralSearchRows(res: unknown): Record<string, unknown>[] {
    if (Array.isArray(res)) {
      return res as Record<string, unknown>[];
    }
    if (res && typeof res === "object") {
      const o = res as Record<string, unknown>;
      for (const key of ["data", "referrals", "results", "items", "body"]) {
        const arr = o[key];
        if (Array.isArray(arr)) {
          return arr as Record<string, unknown>[];
        }
      }
    }
    return [];
  }

  private extractEngageReferralSearchRowId(row: Record<string, unknown>): string {
    return String(row.id ?? row.referralId ?? row.referral_id ?? "").trim();
  }

  private engageReferralSearchRowPhoneDigits(row: Record<string, unknown>): string {
    const raw = String(
      row.phone ?? row.phoneNumber ?? row.phone_number ?? row.phone_mask ?? ""
    );
    return normalizeEngageReferralPhoneDigits(raw);
  }

  /**
   * Worklist Queue column: set Engage referral queue by account queue row id.
   * PATCH `api/v1/referrals/{id}/que-id` with body `{ que_id }` (Engage).
   */
  updateReferralQueId(
    referralId: string,
    queueItemId: string,
    patientId?: string | null
  ): Observable<any> {
    const rid = String(referralId ?? "").trim();
    const qid = String(queueItemId ?? "").trim();
    const id = this.referralDetailsLookupId(patientId, rid);
    if (!id || !qid) {
      return of(null);
    }
    const url = `${environment.engageApiBaseUrl}api/v1/referrals/${encodeURIComponent(id)}/que-id`;
    return this._dataService.patch(url, { que_id: qid }).pipe(
      tap(() => this.invalidateCachedReferralDetails(patientId, rid))
    );
  }

  /**
   * GET PHI `api/v1/referrals?patientId=` — open/closed pipeline deals for scheduling.
   */
  /**
   * Dry-run PHI attachOrOpenReferral (encounter create / schedule placement).
   * Provide encounterId or referralId, not both.
   */
  previewAttachOrOpenReferral(input: {
    encounterId?: string;
    referralId?: string;
    patientId?: string;
    workFlowId?: string;
    scenario?: "new_schedule" | "encounter_audit";
  }): Observable<ReferralAttachOrOpenPreviewResult> {
    const encounterId = String(input.encounterId ?? "").trim();
    const referralId = String(input.referralId ?? "").trim();
    const patientId = String(input.patientId ?? "").trim();
    const workFlowId = String(input.workFlowId ?? "").trim();
    const params = new URLSearchParams();
    if (encounterId) {
      params.set("encounterId", encounterId);
    }
    if (referralId) {
      params.set("referralId", referralId);
    }
    if (patientId) {
      params.set("patientId", patientId);
    }
    if (workFlowId) {
      params.set("workFlowId", workFlowId);
    }
    params.set("scenario", input.scenario ?? "new_schedule");
    const url = `${environment.apiBaseUrl}api/v1/referrals/preview/attach-or-open?${params.toString()}`;
    return this._dataService.get(url) as Observable<ReferralAttachOrOpenPreviewResult>;
  }

  /** POST PHI cleanup — set encounter.referralId to canonical referral id. */
  repointEncounterReferralStamp(input: {
    encounterId: string;
    referralId: string;
  }): Observable<{
    encounterId: string;
    previousReferralId: string | null;
    referralId: string;
  }> {
    const encounterId = String(input.encounterId ?? "").trim();
    const referralId = String(input.referralId ?? "").trim();
    const url = `${environment.apiBaseUrl}api/v1/referrals/cleanup/repoint-encounter-stamp`;
    return this._dataService.post(url, { encounterId, referralId });
  }

  /** POST PHI cleanup — soft-delete duplicate referral merged into canonical id. */
  softDeleteDuplicateReferral(input: {
    duplicateReferralId: string;
    canonicalReferralId: string;
  }): Observable<{
    duplicateReferralId: string;
    canonicalReferralId: string;
    deletedAt: string;
  }> {
    const duplicateReferralId = String(input.duplicateReferralId ?? "").trim();
    const canonicalReferralId = String(input.canonicalReferralId ?? "").trim();
    const url = `${environment.apiBaseUrl}api/v1/referrals/cleanup/soft-delete-duplicate-referral`;
    return this._dataService.post(url, {
      duplicateReferralId,
      canonicalReferralId,
    });
  }

  listPhiReferralsByPatientId(patientId: string): Observable<unknown[]> {
    const pid = String(patientId ?? "").trim();
    if (!pid) {
      return of([]);
    }
    const url = `${environment.apiBaseUrl}api/v1/referrals?patientId=${encodeURIComponent(pid)}`;
    return this._dataService.get(url).pipe(
      map((res: unknown) => {
        if (Array.isArray(res)) {
          return res;
        }
        const o = res as Record<string, unknown> | null;
        if (Array.isArray(o?.["data"])) {
          return o["data"] as unknown[];
        }
        if (Array.isArray(o?.["referrals"])) {
          return o["referrals"] as unknown[];
        }
        return [];
      })
    );
  }

  /** Referral ids whose PHI GET already returned 400. Do not call again this session. */
  private readonly phiReferralGetRejected = new Set<string>();

  /** PHI `GET api/v1/referrals/{referralId}` — workFlowId / currentStageId for prospect deep links. */
  getPhiReferralById(referralId: string): Observable<EngageReferral | null> {
    const rid = String(referralId ?? "").trim();
    if (!rid || this.phiReferralGetRejected.has(rid)) {
      return of(null);
    }
    const url = `${environment.apiBaseUrl}api/v1/referrals/${encodeURIComponent(rid)}`;
    return this._dataService.get(url).pipe(
      map((res: unknown) =>
        res != null && typeof res === "object" && !Array.isArray(res)
          ? (res as EngageReferral)
          : null
      ),
      catchError((err) => {
        if (err?.status === 400) {
          this.phiReferralGetRejected.add(rid);
        }
        return of(null);
      })
    );
  }

  /**
   * Move a referral to another pipeline.
   * PATCH PHI `api/v1/referrals/{referralId}/workflow` with body `{ workFlowId }`.
   */
  updateReferralWorkflow(
    referralId: string,
    workFlowId: string,
    patientId?: string | null
  ): Observable<any> {
    const rid = String(referralId ?? "").trim();
    const wid = String(workFlowId ?? "").trim();
    if (!rid || !wid) {
      return of(null);
    }
    const url = `${environment.apiBaseUrl}api/v1/referrals/${encodeURIComponent(rid)}/workflow`;
    return this._dataService.patch(url, { workFlowId: wid }).pipe(
      tap(() => this.invalidateCachedReferralDetails(patientId, rid))
    );
  }

  /**
   * Move referral to another stage on its work flow (PHI `referrals.currentStageId`).
   * PATCH `api/v1/referrals/{referralId}/stage` with body `{ stageId }` where stageId is work_flow_stages.id.
   */
  updateReferralStage(referralId: string, stageId: string): Observable<any> {
    const rid = String(referralId ?? "").trim();
    const sid = String(stageId ?? "").trim();
    if (!rid || !sid) {
      return of(null);
    }
    const url = `${environment.apiBaseUrl}api/v1/referrals/${encodeURIComponent(rid)}/stage`;
    return this._dataService.patch(url, { stageId: sid }).pipe(
      tap((body) => {
        const resolvedId = String(
          (body as { id?: string } | null)?.id ?? rid
        ).trim();
        this.invalidateCachedReferralDetails(undefined, resolvedId || rid);
      })
    );
  }

  /** Bulk move referrals to a workflow (optional target stage). */
  bulkUpdateReferralWorkflow(payload: {
    referralIds: string[];
    workFlowId: string;
    stageId?: string | null;
  }): Observable<unknown> {
    const referralIds = (payload.referralIds ?? [])
      .map((id) => String(id ?? "").trim())
      .filter(Boolean);
    const workFlowId = String(payload.workFlowId ?? "").trim();
    if (!referralIds.length || !workFlowId) {
      return of(null);
    }
    const body: { referralIds: string[]; workFlowId: string; stageId?: string } = {
      referralIds,
      workFlowId,
    };
    const stageId = String(payload.stageId ?? "").trim();
    if (stageId) {
      body.stageId = stageId;
    }
    const url = `${environment.apiBaseUrl}api/v1/referrals/workflow/bulk`;
    return this._dataService.patch(url, body).pipe(
      tap(() => {
        for (const rid of referralIds) {
          this.invalidateCachedReferralDetails(undefined, rid);
        }
      })
    );
  }

  /** Patch `{ assigned_to }` for the referral (Engage assignment dropdown). */
  updateReferralAssignment(
    referralId: string,
    assignedTo: string | null,
    patientId?: string | null
  ): Observable<any> {
    return this.updateReferral(referralId, { assigned_to: assignedTo }, patientId);
  }

  /**
   * PHI `GET api/v1/patients/directory?page=1&limit=30` with optional `assigned_to` and `ids` query params.
   */
  getReferralsFirstPageWithAssignedToFilter(
    assignedToUserId: string | null,
    referralId?: string | null
  ): Observable<any> {
    const base = phiPatientDirectoryListGetUrl(1, 30);
    const filters: PatientDirectoryFilterValues = {};
    const assignee = (assignedToUserId || "").trim();
    if (assignee) {
      filters.assigned_to = assignee;
    }
    const rid = (referralId || "").trim();
    if (rid) {
      filters.ids = rid;
    }
    const url = appendPatientDirectoryFiltersToUrl(base, filters);
    return this._dataService.get(url);
  }

  /**
   * PHI patient directory for one `patientId` (worklist / schedule prospect resolution).
   */
  getReferralsFirstPageForPatientId(patientId: string): Observable<EngageReferral[]> {
    const pid = (patientId || "").trim();
    if (!pid) {
      return of([]);
    }
    const base = phiPatientDirectoryListGetUrl(1, 30);
    const url = appendPatientDirectoryFiltersToUrl(
      base,
      { patientId: pid },
      { mirrorQueryParams: false }
    );
    return this._dataService.get(url).pipe(
      map((res: unknown) => parsePatientDirectoryOrLegacyReferralsPayload(res, 1, 30).referrals)
    );
  }

  /** PHI directory row(s) for an Engage referral id (`ids` query filter). */
  getReferralsFirstPageForReferralId(referralId: string): Observable<EngageReferral[]> {
    const rid = (referralId || "").trim();
    if (!rid) {
      return of([]);
    }
    return this.getReferralsFirstPageWithAssignedToFilter(null, rid).pipe(
      map(
        (res: unknown) =>
          parsePatientDirectoryOrLegacyReferralsPayload(res, 1, 30).referrals ?? []
      ),
      catchError(() => of([] as EngageReferral[]))
    );
  }

  /**
   * Coordinator assign-to: (1) PATCH referral, (2) GET account `api/v1/users/{id}` when assigned,
   * (3) PHI `GET …/patients/directory` with `assigned_to` when assigned (omit when unassigned).
   */
  runReferralAssignmentUpdateFollowUps(
    referralId: string,
    assignedTo: string | null,
    patientId?: string | null
  ): Observable<void> {
    const rid = String(referralId || '').trim();
    if (!rid) {
      return of(undefined);
    }
    const uid = assignedTo != null ? String(assignedTo).trim() : '';
    return this.updateReferralAssignment(rid, uid || null, patientId).pipe(
      switchMap(() => {
        if (!uid) {
          return this.getReferralsFirstPageWithAssignedToFilter(null, rid).pipe(map(() => undefined));
        }
        return this.getUserById(uid).pipe(
          catchError(() => of(null)),
          switchMap(() => this.getReferralsFirstPageWithAssignedToFilter(uid, rid)),
          map(() => undefined)
        );
      })
    );
  }

  /**
   * Patient directory search — PHI `GET …/patients/directory` with `engage_search` (case-insensitive
   * substring on firstName, lastName, full name, phone digits, or email).
   */
  searchReferrals(searchTerm: string): Observable<ReferralSearchResult[]> {
    const q = (searchTerm || "").trim();
    if (!q) {
      return of([]);
    }
    const limit = 50;
    const base = phiPatientDirectoryListGetUrl(1, limit);
    const url = appendPatientDirectoryFiltersToUrl(base, { engage_search: q });
    return this._dataService.get(url).pipe(
      map((res: unknown) => {
        const parsed = parsePatientDirectoryOrLegacyReferralsPayload(res, 1, limit);
        const out: ReferralSearchResult[] = [];
        for (const row of parsed.referrals ?? []) {
          const mapped = this.mapDirectoryRowToReferralSearchResult(row);
          if ((mapped.id || "").trim()) {
            out.push(mapped);
          }
        }
        return out;
      }),
      catchError(() => of([]))
    );
  }

  private mapDirectoryRowToReferralSearchResult(row: EngageReferral): ReferralSearchResult {
    const r = row as Record<string, unknown>;
    const id = String(r.id ?? r.referralId ?? r.referral_id ?? "").trim();
    let firstName = String(row.firstName ?? r.first_name ?? r.givenName ?? "").trim();
    let lastName = String(row.lastName ?? r.last_name ?? r.familyName ?? "").trim();
    if (!firstName && !lastName) {
      const full = String(
        r.full_name ??
          r.fullName ??
          r.name ??
          r.patientName ??
          r.patient_name ??
          ""
      ).trim();
      if (full) {
        const parts = full.split(/\s+/).filter(Boolean);
        firstName = parts[0] ?? "";
        lastName = parts.slice(1).join(" ");
      }
    }
    const phone = String(
      row.phone ?? r.phoneNumber ?? r.phone_number ?? r.mobile ?? ""
    ).trim();
    const email = String(row.email ?? r.emailAddress ?? "").trim();
    const createdAt = String(row.createdAt ?? r.created_at ?? "").trim();
    let patientId = String(
      row.patientId ?? r.patient_id ?? r.PatientId ?? r.patientID ?? ""
    ).trim();
    if (!patientId) {
      const pidArr = r.patientIds ?? r.patient_ids;
      if (Array.isArray(pidArr)) {
        for (const x of pidArr) {
          const s = String(x ?? "").trim();
          if (s) {
            patientId = s;
            break;
          }
        }
      }
    }
    return {
      id,
      ...(patientId ? { patientId } : {}),
      firstName,
      lastName,
      phone,
      email,
      createdAt,
    };
  }

  /**
   * Engage tag search for notes / mentions.
   * GET api/v1/tags/search?q=&lead_status= (lead_status optional, exact match filter)
   */
  searchTags(q: string, leadStatus?: string | null): Observable<EngageTagSearchResult[]> {
    const params = new URLSearchParams();
    params.set('q', q ?? '');
    const ls = (leadStatus ?? '').trim();
    if (ls) {
      params.set('lead_status', ls);
    }
    const url = `${environment.adminApiBaseUrl}api/v1/tags/search?${params.toString()}`;
    return this._dataService.get(url).pipe(
      map((res: any) => {
        if (Array.isArray(res)) {
          return res as EngageTagSearchResult[];
        }
        if (Array.isArray(res?.data)) {
          return res.data as EngageTagSearchResult[];
        }
        return [];
      })
    );
  }

  // Activity APIs (Engage)
  createActivity(activity: any): Observable<any> {
    const url = `${environment.engageApiBaseUrl}api/v1/activity`;
    return this._dataService.post(url, activity);
  }

  /**
   * PATCH `clear-reminder-notification/:id` — on many Engage builds this `:id` is the **activity** id
   * (same row as `reset-notifications/:id`), not `api/v1/reminders/:id`. Prefer resolving via
   * `EngageActivityService.getReferralActivities` + `resetNotifications` from the reminder UI.
   */
  clearReminderNotification(activityOrReminderId: string): Observable<any> {
    const url = `${environment.engageApiBaseUrl}api/v1/activity/clear-reminder-notification/${encodeURIComponent(activityOrReminderId)}`;
    return this._dataService.patch(url, {});
  }

  // Engage Events API - clinic scheduled (path = Engage referral id)
  createClinicEvent(
    referralId: string,
    payload: {
      eventType: string;
      metadata: Record<string, unknown>;
      triggeredBy: string;
      encounterId: string;
      source?: string;
      patientId?: string;
    }
  ): Observable<any> {
    const url = `${environment.engageApiBaseUrl}api/v1/engage/events/clinic/${encodeURIComponent(referralId.trim())}`;
    return this._dataService.patch(url, payload);
  }

  // ----------------------------
  // Account (admin) user APIs
  // ----------------------------

  getUserById(userId: string): Observable<any> {
    const url = `${environment.adminApiBaseUrl}api/v1/users/${encodeURIComponent(userId)}`;
    return this._dataService.get(url);
  }

  // ----------------------------
  // PHI patient APIs
  // ----------------------------

  createPhiPatient(payload: any): Observable<any> {
    const url = `${environment.apiBaseUrl}api/v1/patients`;
    return this._dataService.post(url, payload);
  }

  updatePatient(patientId: string, patient: any): Observable<any> {
    const url = `${environment.apiBaseUrl}api/v1/patients/${encodeURIComponent(patientId)}`;
    return this._dataService.patch(url, patient);
  }

  searchPhiPatientsByTerm(term: string): Observable<any[]> {
    if (!term) {
      return of([]);
    }
    const url = `${environment.apiBaseUrl}api/v1/patients/search/term?term=${encodeURIComponent(term)}`;
    return this._dataService.get(url);
  }

  // ----------------------------
  // PHI encounter APIs
  // ----------------------------

  createEncounter(payload: any): Observable<any> {
    const url = `${environment.apiBaseUrl}api/v1/encounters`;
    return this._dataService.post(url, payload);
  }

  getEncountersForDate(scheduleDate: string): Observable<any[]> {
    const dateObj = DateTime.fromISO(scheduleDate);
    const payload = {
      providers: [],
      resources: [],
      filterStatus: [],
      filterLocation: [],
      visitType: [],
      filterDate: {
        startDate: dateObj.startOf('day').toFormat("yyyy-MM-dd'T'HH:mm:ss"),
        endDate: dateObj.endOf('day').toFormat("yyyy-MM-dd'T'HH:mm:ss"),
      },
      pageStart: 0,
      pageLength: 1000,
      sortField: 'encounterDateTime',
      sortDirection: 'DESC' as 'DESC',
      searchQuery: '',
    };
    const url = `${environment.apiBaseUrl}api/v1/encounters/list-clinic`;
    return this._dataService.post(url, payload).pipe(map((response: any) => response?.data || []));
  }

  getEncountersByPatientId(patientId: string): Observable<any[]> {
    const url = `${environment.apiBaseUrl}api/v1/encounters/patient/${encodeURIComponent(patientId)}`;
    return this._dataService.get(url);
  }

  getEncounterDetails(encounterId: string): Observable<any> {
    const url = `${environment.apiBaseUrl}api/v1/encounters/details/${encodeURIComponent(encounterId)}`;
    return this._dataService.get(url);
  }

  updateEncounterSchedule(payload: any): Observable<any> {
    const url = `${environment.apiBaseUrl}api/v1/encounter-schedule`;
    return this._dataService.post(url, payload);
  }

  updateEncounter(encounterId: string, payload: any): Observable<any> {
    const url = `${environment.apiBaseUrl}api/v1/encounters/${encodeURIComponent(encounterId)}`;
    return this._dataService.patch(url, payload).pipe(
      switchMap((updated) => {
        const scheduleStatus =
          resolveEncounterScheduleStatus(updated) ||
          resolveEncounterScheduleStatus(payload);
        return this._encounterClaimStatusSyncService
          .ensureDraftWhenSubmittedIfClaimStatusEmpty(
            encounterId,
            scheduleStatus,
            [updated, payload]
          )
          .pipe(map(() => updated));
      })
    );
  }
}

export function extractEngageReferralRowId(row: unknown): string {
  if (row == null) {
    return "";
  }
  if (typeof row === "string" || typeof row === "number") {
    return String(row).trim();
  }
  if (typeof row !== "object") {
    return "";
  }
  const o = row as Record<string, unknown>;
  for (const k of ["referralId", "referral_id", "referralID", "id"] as const) {
    const v = o[k];
    if (v != null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  return "";
}

/**
 * Prefer PHI `referrals.id` from directory rows when Engage `id` is a separate CRM key.
 */
export function extractPhiReferralIdForStagePatch(row: unknown): string {
  if (row == null || typeof row !== "object") {
    return extractEngageReferralRowId(row);
  }
  const o = row as Record<string, unknown>;
  const engageRowId = String(o["id"] ?? "").trim();
  for (const key of [
    "phiReferralId",
    "clinicReferralId",
    "referral_id",
    "referralId",
  ] as const) {
    const value = String(o[key] ?? "").trim();
    if (!value) {
      continue;
    }
    if (
      key === "referralId" &&
      engageRowId &&
      value.toLowerCase() === engageRowId.toLowerCase()
    ) {
      continue;
    }
    return value;
  }
  return extractEngageReferralRowId(row);
}

/** Ignore a value when it is the same UUID as the Engage referral row id (common URL/bootstrap mistake). */
export function resolveClinicPatientIdDistinctFromReferral(
  patientIdCandidate: string | null | undefined,
  referralId: string | null | undefined
): string {
  const p = String(patientIdCandidate ?? "").trim();
  const r = String(referralId ?? "").trim();
  if (!p) {
    return "";
  }
  if (r && p.toLowerCase() === r.toLowerCase()) {
    return "";
  }
  return p;
}

/** Clinic PHI patient id from a referral / directory row (camelCase, snake_case, nested patient). */
export function extractEngageReferralPatientId(
  ref: EngageReferral | Record<string, unknown> | null | undefined
): string {
  const referralId = extractEngageReferralRowId(ref);
  const row = ref as (EngageReferral & Record<string, unknown>) | null;
  const direct = String(row?.patientId ?? "").trim();
  if (direct) {
    return resolveClinicPatientIdDistinctFromReferral(direct, referralId);
  }
  const altKeys = [
    "patient_id",
    "PatientId",
    "phiPatientId",
    "clinicPatientId",
    "clinic_patient_id",
  ];
  for (const key of altKeys) {
    const v = String(row?.[key] ?? "").trim();
    if (v) {
      return resolveClinicPatientIdDistinctFromReferral(v, referralId);
    }
  }
  const pidArr = row?.patientIds;
  if (Array.isArray(pidArr) && pidArr.length > 0) {
    const first = String(pidArr[0] ?? "").trim();
    if (first) {
      return resolveClinicPatientIdDistinctFromReferral(first, referralId);
    }
  }
  const nestedNodes: unknown[] = [
    row?.["patient"],
    row?.["Patient"],
    (row?.["referral"] as Record<string, unknown> | undefined)?.["patient"],
    row?.["encountersPatient"],
    row?.["EncountersPatient"],
  ];
  for (const node of nestedNodes) {
    if (node == null || typeof node !== "object" || Array.isArray(node)) {
      continue;
    }
    const n = node as Record<string, unknown>;
    const fromNested = String(
      n["id"] ?? n["patientId"] ?? n["patient_id"] ?? ""
    ).trim();
    if (fromNested) {
      return resolveClinicPatientIdDistinctFromReferral(fromNested, referralId);
    }
  }
  return "";
}

/**
 * After merging a list row with flattened details, keep the list GET /referrals identity.
 * Nested `data` often carries a different `id` (e.g. patient) that must not replace the referral id.
 */
export function preserveEngageReferralIdFromListRow(
  listRow: EngageReferral | null | undefined,
  merged: EngageReferral
): EngageReferral {
  const stable = extractEngageReferralRowId(listRow);
  if (!stable) {
    return merged;
  }
  return { ...merged, id: stable, referralId: stable } as EngageReferral;
}

/**
 * Unwraps nested Engage referral-details payloads (`data`, `body`, `result`) onto a flat object
 * so merges and {@link extractEngageReferralSourceLabel} see `referralSource` / `referral_source` at the top level.
 */
export function flattenEngageReferralDetailsResponse(details: unknown): Record<string, unknown> {
  if (details == null || typeof details !== "object") {
    return {};
  }
  const d = details as Record<string, unknown>;
  const out: Record<string, unknown> = { ...d };
  const inner = d["data"] ?? d["body"] ?? d["result"];
  if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
    Object.assign(out, inner as Record<string, unknown>);
  }
  const envelopeReferralId = extractEngageReferralRowId(d);
  if (envelopeReferralId) {
    out["id"] = envelopeReferralId;
    out["referralId"] = envelopeReferralId;
  }
  return out;
}

function isValidEngageLastActivityPayload(
  x: unknown
): x is EngageReferralLastActivity {
  if (!x || typeof x !== "object" || Array.isArray(x)) {
    return false;
  }
  const o = x as Record<string, unknown>;
  return (
    o["type"] != null ||
    o["comment"] != null ||
    o["name"] != null
  );
}

/**
 * Directory rows may omit `last_activity` at root (same as some legacy Engage envelopes); activity feeds supplement display.
 * Use this before falling back to the activity feed (feed order can differ from Engage list `last_activity`).
 */
export function extractLastActivityFromReferralDetailsPayload(
  details: unknown
): EngageReferralLastActivity | undefined {
  if (details == null) {
    return undefined;
  }
  const flat = flattenEngageReferralDetailsResponse(details);
  const direct = flat["last_activity"] ?? flat["lastActivity"];
  if (isValidEngageLastActivityPayload(direct)) {
    return direct;
  }
  const d = details as Record<string, unknown>;
  const inner = d["data"] ?? d["body"] ?? d["result"];
  const nestedContainers: unknown[] = [
    d["referral"],
    flat["referral"],
    inner && typeof inner === "object" && !Array.isArray(inner)
      ? (inner as Record<string, unknown>)["referral"]
      : undefined,
  ];
  for (const node of nestedContainers) {
    if (node == null || typeof node !== "object" || Array.isArray(node)) {
      continue;
    }
    const n = node as Record<string, unknown>;
    const la = n["last_activity"] ?? n["lastActivity"];
    if (isValidEngageLastActivityPayload(la)) {
      return la;
    }
  }
  return undefined;
}

/**
 * First row from PHI directory / legacy referrals list payloads (`items`, `data`, `referrals`, …).
 */
export function firstReferralFromReferralsListResponse(
  res: unknown
): EngageReferral | null {
  if (res == null) {
    return null;
  }
  if (Array.isArray(res)) {
    return res.length > 0 ? (res[0] as EngageReferral) : null;
  }
  if (typeof res !== "object") {
    return null;
  }
  const r = res as Record<string, unknown>;
  const rows = Array.isArray(r["items"])
    ? r["items"]
    : Array.isArray(r["data"])
      ? r["data"]
      : Array.isArray(r["referrals"])
        ? r["referrals"]
        : Array.isArray(r["body"])
          ? r["body"]
          : null;
  if (!rows || rows.length === 0) {
    return null;
  }
  return rows[0] as EngageReferral;
}

/** One row from GET admin `api/v1/users/referral-sources` (id + coordinator-style name). */
export interface ReferralSourceCatalogEntry {
  id: string;
  label: string;
}

/** Build a lookup table from {@link AuthenticationService#getReferralSources} (same mapping as add-referral / dashboard). */
export function buildReferralSourceCatalogFromApi(sources: unknown): ReferralSourceCatalogEntry[] {
  if (!Array.isArray(sources)) {
    return [];
  }
  const out: ReferralSourceCatalogEntry[] = [];
  for (const item of sources) {
    if (typeof item === "string") {
      const s = item.trim();
      if (s) {
        out.push({ id: s, label: s });
      }
      continue;
    }
    if (item != null && typeof item === "object") {
      const src = item as Record<string, unknown>;
      let id = String(src["id"] ?? src["value"] ?? "").trim();
      if (!id) {
        continue;
      }
      const fn = src["firstName"] ?? src["first_name"];
      const ln = src["lastName"] ?? src["last_name"];
      const name = [fn, ln]
        .map((x) => (x != null ? String(x).trim() : ""))
        .filter(Boolean)
        .join(" ");
      let label =
        name ||
        String(src["displayName"] ?? src["label"] ?? src["name"] ?? "").trim();
      if (id.toLowerCase() === "meta") {
        id = "online";
        label = "Online";
      }
      if (!label) {
        label = id;
      }
      out.push({ id, label });
    }
  }
  return out;
}

export function resolveReferralSourceIdWithCatalog(
  id: string,
  catalog: readonly ReferralSourceCatalogEntry[]
): string {
  const k = String(id || "").trim();
  if (!k) {
    return "";
  }
  const hit = catalog.find(
    (e) => e.id === k || String(e.id).toLowerCase() === k.toLowerCase()
  );
  return hit?.label?.trim() || "";
}

/**
 * Normalize referral `source` from APIs that return either a uuid string/number or
 * `{ source, source_name }` (and similar id keys).
 */
export function normalizeEngageReferralSourceIdField(raw: unknown): string {
  if (raw == null) {
    return "";
  }
  if (typeof raw === "string") {
    return raw.trim();
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(raw);
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    for (const k of ["source", "id", "source_id", "sourceId"]) {
      const v = o[k];
      if (typeof v === "string" && v.trim()) {
        return v.trim();
      }
      if (typeof v === "number" && Number.isFinite(v)) {
        return String(v);
      }
    }
  }
  return "";
}

/** Explicit id fields when the API returns a UUID instead of a display string. */
export function extractEngageReferralSourceId(data: unknown, depth = 2): string {
  if (data == null || typeof data !== "object") {
    return "";
  }
  const o = data as Record<string, unknown>;
  const keys = [
    "referral_source_id",
    "referralSourceId",
    "referral_source_uuid",
    "referralSourceUUID",
    "source",
  ];
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) {
      return v.trim();
    }
    if (typeof v === "number" && Number.isFinite(v)) {
      return String(v);
    }
  }
  if (depth > 0) {
    for (const nestKey of ["referral_source", "referralSource"]) {
      const nest = o[nestKey];
      if (nest != null && typeof nest === "object" && !Array.isArray(nest)) {
        const inner = extractEngageReferralSourceId(nest, depth - 1);
        if (inner) {
          return inner;
        }
        const flat = normalizeEngageReferralSourceIdField(nest);
        if (flat) {
          return flat;
        }
      }
    }
    const src = o["source"];
    if (src != null && typeof src === "object" && !Array.isArray(src)) {
      const inner = extractEngageReferralSourceId(src, depth - 1);
      if (inner) {
        return inner;
      }
    }
    for (const nestKey of ["referral", "data", "body", "details"]) {
      const nest = o[nestKey];
      if (nest != null && typeof nest === "object" && !Array.isArray(nest)) {
        const inner = extractEngageReferralSourceId(nest, depth - 1);
        if (inner) {
          return inner;
        }
      }
    }
  }
  return "";
}

/**
 * Referral list rows and PHI directory detail payloads may differ from nested Engage shapes; normalize to a display string.
 */
export function extractEngageReferralSourceLabel(data: unknown, depth = 2): string {
  if (data == null || typeof data !== "object") {
    return "";
  }
  const o = data as Record<string, unknown>;
  const tryString = (v: unknown): string => {
    if (v == null) {
      return "";
    }
    if (typeof v === "string") {
      return v.trim();
    }
    if (typeof v === "number" || typeof v === "boolean") {
      return String(v).trim();
    }
    if (typeof v === "object") {
      const rec = v as Record<string, unknown>;
      const name =
        rec["source_name"] ??
        rec["sourceName"] ??
        rec["name"] ??
        rec["displayName"] ??
        rec["label"] ??
        rec["title"];
      if (name != null && String(name).trim() !== "") {
        return String(name).trim();
      }
      const fn = rec["firstName"];
      const ln = rec["lastName"];
      const joined = [fn, ln]
        .map((x) => (x != null ? String(x).trim() : ""))
        .filter(Boolean)
        .join(" ");
      if (joined) {
        return joined;
      }
    }
    return "";
  };
  const keys = [
    "source_name",
    "sourceName",
    "referral_source",
    "referralSource",
    "referral_source_name",
    "referralSourceName",
    "referral_source_label",
    "referralSourceLabel",
    "referral_source_display",
    "lead_source",
    "leadSource",
    "source",
    "sourceName",
    "source_label",
    "referral",
  ];
  /** Do not use `channel` (form/web/sms/…) — it is not referral source; null RS must stay empty → UI shows "--". */
  for (const k of keys) {
    const s = tryString(o[k]);
    if (s) {
      return s;
    }
  }
  if (depth > 0) {
    for (const nestKey of ["referral_source", "referralSource", "referral", "data", "body", "details"]) {
      const nest = o[nestKey];
      if (nest != null && typeof nest === "object" && !Array.isArray(nest)) {
        const nestedLabel = extractEngageReferralSourceLabel(nest, depth - 1);
        if (nestedLabel) {
          return nestedLabel;
        }
      }
    }
  }
  return "";
}

